// Ported from dev-scrips-pm-react services/scheduling/use-provider-slots.ts (PROD-1190 calendar port)
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type {
  AppointmentSlot,
  CreateAppointmentProfileRequest,
  ProviderDetailResponse,
} from '@/types/scheduling-api';

/**
 * POST /api/Doctor/ProviderSlots
 *
 * Mirrors Flutter `searchDoctor()` / `DoctorListRequest` —
 * scheduling_data_source_impl.dart:691–712.
 * Used by the create-appointment panel; NOT the same as
 * /api/Doctor/AvailableProviders (which the calendar provider strip uses).
 *
 * The response is `DoctorListResponse[]` — each entry carries the doctor
 * plus their working hours, slots, and (sometimes) appointment profiles.
 */

/** Working-hour entry per `WorkingHour` in Flutter `doctor_list_response.dart:420`. */
export interface WorkingHour {
  weedayId?: number;
  startWeekDay?: string;
  endWeekDay?: string;
  /** Local time string, e.g. "08:00 AM". */
  startTime?: string;
  /** Local time string, e.g. "11:00 PM". */
  endTime?: string;
  dayOff?: unknown;
}

export interface ProviderSlotsResponse extends ProviderDetailResponse {
  /** Profiles for this provider — sometimes inlined by the backend. */
  appointmentProfile?: CreateAppointmentProfileRequest[];
  workingHourId?: string;
  workingHours?: WorkingHour[];
  /** Inline slots — populated when the request sets `searchSlot: true`. */
  slots?: AppointmentSlot[];
  totalSlots?: number;
}

interface UseProviderSlotsParams {
  organizationId: string;
  practiceId: string;
  /** ISO date — the hook expands to start/end of day. */
  date: string;
  /** Location IDs filter. Send `[]` to query without a location filter. */
  locationIds?: string[];
  searchText?: string;
  /**
   * Practitioner ID filter. The .NET backend distinguishes `null`
   * (no filter — return all) from `[]` (filter to zero practitioners
   * — return nothing, sometimes a 500 when combined with an empty
   * `location`). Working dev curl ships `null` for the unfiltered
   * case. Verified via /api/Doctor/ProviderSlots curl 2026-05-18:
   * the BlockAvailability panel surfaced a 500 here because the
   * legacy default was `[]`; appointment panel hid this by always
   * passing a non-empty locationIds. Default below changed to `null`
   * so the unfiltered shape is correct out of the box.
   */
  practitioners?: string[] | null;
  pageNumber?: number;
  pageSize?: number;
  /** Number of preview slots to include per provider. 0 = no slots. */
  slotCount?: number;
  /** When true, backend includes `slots[]` on each provider. */
  searchSlot?: boolean;
  /**
   * Backend appointmentProfileType code. UI labels are "First" / "Follow Up"
   * but the wire enum is `EnumAppointmentProfileType` with values from
   * Flutter `app_constants.dart:691–692`.
   */
  appointmentProfileType?: 'ROUTINE' | 'FOLLOWUP' | null;
}

/**
 * Build the day-range strings in the exact shape Flutter's
 * `DoctorListRequest.toJson()` produces — local-clock components stamped
 * with a `Z` suffix (it's not actually UTC; the backend treats the value
 * as a wall-clock date). Using `setUTCHours` on a local Date would shift
 * to the previous UTC day for users east of UTC and produce a date that's
 * one day behind the user's current calendar day.
 */
function toUtcRange(date: string): { startDate: string; endDate: string } {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return {
    startDate: `${y}-${m}-${day}T00:00:00Z`,
    endDate: `${y}-${m}-${day}T23:59:59Z`,
  };
}

export function useProviderSlots(params: UseProviderSlotsParams | null) {
  return useQuery({
    queryKey: ['provider-slots', params],
    queryFn: ({ signal }) => {
      if (!params) throw new Error('useProviderSlots called without params');
      const { startDate, endDate } = toUtcRange(params.date);
      const body = {
        organizationId: params.organizationId,
        practiceId: params.practiceId,
        startDate,
        endDate,
        searchText: params.searchText ?? '',
        slotCount: params.slotCount ?? 0,
        searchSlot: params.searchSlot ?? false,
        appointmentProfileType: params.appointmentProfileType ?? null,
        location: params.locationIds ?? [],
        isInAppEnabled: false,
        isSearchFromDashboard: false,
        pageNumber: params.pageNumber ?? 0,
        pageSize: params.pageSize ?? 5,
        // null (not []) — see JSDoc above. Empty array gets the
        // .NET binder to reject when location is also empty.
        practitioners: params.practitioners ?? null,
        specialitySkillId: null,
      };
      return post<ProviderSlotsResponse[]>('/api/Doctor/ProviderSlots', body, { signal });
    },
    enabled: !!params?.practiceId && !!params?.organizationId && !!params?.date,
    placeholderData: keepPreviousData,
  });
}
